/**
 * Shared Supabase client factory for multi-app architecture.
 *
 * Both Litopia and OpenPenPal import this to get a configured
 * Supabase client pointing at the same self-hosted backend.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';

export type Platform = 'litopia' | 'openpenpal';

export interface CreateSupabaseOptions {
  url: string;
  anonKey: string;
  platform: Platform;
  appVersion?: string;
  /** True inside Capacitor or Tauri WebView */
  isNative?: boolean;
  /** Disable automatic callback consumption when the host owns guarded auth routes. */
  detectSessionInUrl?: boolean;
  /** Custom fetch implementation for non-browser runtimes such as WeChat Mini Program */
  fetch?: typeof fetch;
  /** Custom storage adapter (e.g. Taro Storage for mini programs) */
  storage?: {
    getItem: (key: string) => string | null | Promise<string | null>;
    setItem: (key: string, value: string) => void | Promise<void>;
    removeItem: (key: string) => void | Promise<void>;
  };
}

let _instance: SupabaseClient | null = null;
let _platform: Platform = 'litopia';
const clientListeners = new Set<() => void>();

function notifyClientListeners() {
  clientListeners.forEach((listener) => listener());
}

/**
 * Create (or return cached) Supabase client.
 * Call once at app startup; subsequent calls return the same instance.
 */
export function createSupabaseClient(opts: CreateSupabaseOptions): SupabaseClient {
  if (_instance) return _instance;

  _platform = opts.platform;

  const runtimeFetch = opts.fetch ?? (
    typeof window !== 'undefined' && typeof window.fetch === 'function'
      ? window.fetch.bind(window)
      : undefined
  );

  if (!runtimeFetch) {
    throw new Error('Supabase fetch implementation is unavailable for this runtime');
  }

  _instance = createClient(opts.url, opts.anonKey, {
    auth: {
      storage: opts.storage ?? (typeof localStorage !== 'undefined' ? localStorage : undefined),
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: opts.detectSessionInUrl ?? (typeof window !== 'undefined'),
      flowType: 'pkce',
    },
    global: {
      headers: {
        'X-Client-Platform': opts.platform,
        'X-Client-Version': opts.appVersion ?? '1.0.0',
      },
      fetch: (url, options = {}) => {
        if (opts.isNative) {
          return runtimeFetch(url, options);
        }
        const controller = new AbortController();
        // The timeout has to add to the caller's cancellation, not overwrite it:
        // whatever handed a signal here (React Query dropping its last observer, an
        // abandoned upload) expects the request to stop when it says so.
        const callerSignal = options.signal ?? undefined;
        const abortFromCaller = () => controller.abort(callerSignal?.reason);
        if (callerSignal) {
          if (callerSignal.aborted) abortFromCaller();
          else callerSignal.addEventListener('abort', abortFromCaller, { once: true });
        }
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        return runtimeFetch(url, {
          ...options,
          signal: controller.signal,
        }).finally(() => {
          clearTimeout(timeoutId);
          // A caller signal can outlive every request it gates, so the listener has
          // to go with the request.
          callerSignal?.removeEventListener('abort', abortFromCaller);
        });
      },
    },
    realtime: {
      params: { eventsPerSecond: 2 },
    },
  });
  notifyClientListeners();

  // Handle refresh token errors
  if (typeof window !== 'undefined' && !opts.isNative) {
    const originalFetch = window.fetch;
    window.fetch = async (...args: Parameters<typeof window.fetch>) => {
      const url = args[0]?.toString() || '';
      if (url.startsWith('data:') || url.startsWith('blob:')) {
        return originalFetch(...args);
      }
      const response = await originalFetch(...args);
      if (
        response.status === 400 &&
        url.includes('/auth/v1/token?grant_type=refresh_token')
      ) {
        console.warn('[shared] Invalid refresh token. Clearing session...');
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-')) localStorage.removeItem(key);
        });
        window.location.reload();
      }
      return response;
    };
  }

  return _instance;
}

/**
 * Return the shared Supabase client, creating it from the supplied options when needed.
 * Reuses an existing client for the same platform to avoid duplicate auth storage and realtime sessions.
 */
export function ensureSupabaseClient(opts: CreateSupabaseOptions): SupabaseClient {
  return createSupabaseClient(opts);
}

/** Clear the cached shared client so a later call can initialize a fresh session. */
export function resetSupabaseClient(): void {
  _instance = null;
  _platform = 'litopia';
  notifyClientListeners();
}

/**
 * Register a host application's already-created client.
 *
 * The embedded OpenPenPal app runs inside Litopia, whose shell already owns
 * the auth client. Reusing it prevents two GoTrue clients from competing for
 * the same persisted session in one WebView.
 */
export function registerSupabaseClient(
  client: SupabaseClient,
  platform: Platform = 'litopia',
): SupabaseClient {
  if (!_instance) {
    _instance = client;
    _platform = platform;
    notifyClientListeners();
  }
  return _instance;
}

/** Get the cached client (throws if not yet created). */
export function getSupabase(): SupabaseClient {
  if (!_instance) throw new Error('Supabase client not initialized. Call createSupabaseClient() first.');
  return _instance;
}

/** Return the cached shared client without initializing or throwing when it is unavailable. */
export function getSupabaseOrNull(): SupabaseClient | null {
  return _instance;
}

/** Report whether a shared Supabase client has already been initialized. */
export function isSupabaseClientInitialized(): boolean {
  return Boolean(_instance);
}

/** Subscribe to shared-client registration/reset without polling. */
export function subscribeSupabaseClient(listener: () => void): () => void {
  clientListeners.add(listener);
  return () => clientListeners.delete(listener);
}

/** Get which platform the client was created for. */
export function getCurrentPlatform(): Platform {
  return _platform;
}
